// AIDialerBotTrainer.jsx — train an AI Dialer screener bot end-to-end.
//
// Sections (single-scroll page):
//   A. Identity              — name, description, photo
//   B. Voice                 — stock voice OR clone-my-voice OR pick-existing-clone
//   C. Script + Personality  — PDF/TXT/MD upload → GPT-4o-mini structured extract → editable
//   D. Few-shot examples     — past call recordings / transcripts (optional)
//   E. Test                  — text-mode roleplay preview (no audio yet)
//   F. Save                  — POST /api/dialer/v1/bots
//
// Save creates a DialerBotV1 doc; once saved it shows up in the campaign
// creator step 1 ("My trained bots").

import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Bot, Upload, Mic, Trash2, Plus, Save, Loader2, Volume2, FileText, MessageCircle,
  CheckCircle2, Sparkles, Play, X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import TopBar from '../components/TopBar.jsx';
import { api, apiGet, apiPost, apiDel } from '../lib/api.js';
import { mergeServerBot } from '../lib/dialerBotHydration.js';
import BotAudioTest from '../components/dialer/BotAudioTest.jsx';
import { planCompression, compressAudioFile, formatCompressionSummary } from '../lib/compressAudio.js';
import { pollExtractJob } from '../lib/pollExtractJob.js';
import TrainingMaterialsList from '../components/TrainingMaterialsList.jsx';

const STOCK_VOICES = [
  { id: 'pNInz6obpgDQGcFmaJgB', label: 'Adam (warm, male)' },
  { id: 'EXAVITQu4vr4xnSDxMaL', label: 'Sarah (friendly, female)' },
  { id: 'CYw3kZ02Hs0563khs1Fj', label: 'Dave (calm, male)' },
  { id: 'XrExE9yKIg1WjnnlVkGX', label: 'Matilda (energetic, female)' },
];

// OpenAI Realtime voices for the AI test call (browser WebSocket).
// These are SEPARATE from ElevenLabs voiceId — a different voice system.
const REALTIME_VOICES = [
  { id: 'ash',     label: 'Ash (neutral, male)' },
  { id: 'alloy',   label: 'Alloy (neutral)' },
  { id: 'ballad',  label: 'Ballad (warm, male)' },
  { id: 'coral',   label: 'Coral (warm, female)' },
  { id: 'echo',    label: 'Echo (neutral, male)' },
  { id: 'marin',   label: 'Marin (clear, female)' },
  { id: 'cedar',   label: 'Cedar (calm, male)' },
  { id: 'sage',    label: 'Sage (calm, female)' },
  { id: 'shimmer', label: 'Shimmer (bright, female)' },
  { id: 'verse',   label: 'Verse (expressive, male)' },
];

export default function AIDialerBotTrainer() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const [searchParams] = useSearchParams();
  const initialBotId = searchParams.get('id');

  const [bot, setBot] = useState({
    _id: initialBotId || undefined,
    name: '',
    description: '',
    photo: '',
    voiceSource: 'stock',
    voiceId: 'pNInz6obpgDQGcFmaJgB',
    voiceLabel: 'Adam (warm, male)',
    realtimeVoice: 'ash',
    openingLine: '',
    qualifyingQuestions: [],
    objectionHandlers: [],
    closingScript: '',
    personalityNotes: '',
    fewShotExamples: [],
  });

  const { data: meData } = useQuery({
    queryKey: ['me'],
    queryFn: () => apiGet('/auth/me'),
  });
  const trainedVoices = meData?.data?.agent?.dialerSettings?.trainedVoices
                     || meData?.agent?.dialerSettings?.trainedVoices
                     || [];

  // Preload existing bot if ?id=. React Query v5 dropped the useQuery
  // onSuccess option, so we hydrate via useEffect once per id and merge the
  // server fields onto the default-shaped local state — older saved bots may
  // be missing newer fields (e.g. fewShotExamples), which would otherwise
  // crash the form.
  const { data: botResp, isLoading: botLoading } = useQuery({
    queryKey: ['dialer-bot', initialBotId],
    queryFn: () => apiGet(`/dialer/v1/bots/${initialBotId}`),
    enabled: !!initialBotId,
  });

  const hydratedRef = useRef(false);
  useEffect(() => { hydratedRef.current = false; }, [initialBotId]);
  useEffect(() => {
    const serverBot = botResp?.data;
    if (!serverBot || hydratedRef.current) return;
    setBot((b) => mergeServerBot(b, serverBot));
    hydratedRef.current = true;
  }, [botResp]);

  const update = (patch) => setBot((b) => ({ ...b, ...patch }));

  const saveMut = useMutation({
    mutationFn: (status) => apiPost('/dialer/v1/bots', { ...bot, status: status || 'draft' }),
    onSuccess: (r) => {
      toast.success('Bot saved');
      qc.invalidateQueries({ queryKey: ['dialer-v1-bots'] });
      const id = r?.data?._id;
      if (id) {
        qc.invalidateQueries({ queryKey: ['bot-training-materials', id] });
        // Server stamps materialIds on persisted few-shot rows — re-sync local
        // state so subsequent saves don't re-create training-material rows.
        if (r.data) setBot((b) => mergeServerBot(b, r.data));
        nav(`/ai-dialer/bots/train?id=${id}`, { replace: true });
      }
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Save failed'),
  });

  const [confirmDelete, setConfirmDelete] = useState(false);
  const deleteMut = useMutation({
    mutationFn: () => apiDel(`/dialer/v1/bots/${bot._id}`),
    onSuccess: () => {
      toast.success('Bot deleted');
      qc.invalidateQueries({ queryKey: ['dialer-v1-bots'] });
      setConfirmDelete(false);
      nav('/ai-dialer?tab=campaigns');
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Delete failed'),
  });

  return (
    <div className="space-y-4 pb-12">
      <TopBar
        title="Train AI Dialer Bot"
        subtitle="Build a screener that sounds like you, asks your questions, and handles your objections."
      />

      {botLoading && initialBotId && (
        <div className="text-xs text-white/50 flex items-center gap-2 px-1">
          <Loader2 className="w-3 h-3 animate-spin" /> Loading bot…
        </div>
      )}

      <SectionA bot={bot} update={update} />
      <SectionB bot={bot} update={update} trainedVoices={trainedVoices} />
      <SectionC bot={bot} update={update} />
      <SectionD bot={bot} update={update} />
      <SectionE bot={bot} />

      {bot._id && (
        <div>
          <div className="text-xs text-white/40 px-1 mb-1 uppercase tracking-wider">
            Training history
          </div>
          <TrainingMaterialsList botId={bot._id} />
        </div>
      )}

      <div className="panel p-4 flex items-center justify-end gap-3 sticky bottom-0 bg-civix-panel/95 backdrop-blur-sm">
        {!bot.name.trim() && (
          <span className="text-xs text-white/40 mr-auto">Add a bot name in Identity to enable Save.</span>
        )}
        {bot._id && (
          <button
            onClick={() => setConfirmDelete(true)}
            disabled={deleteMut.isPending}
            className="flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-red-400 border border-red-500/40 hover:bg-red-500/10 disabled:opacity-50"
            title="Delete this bot"
          >
            <Trash2 className="w-4 h-4" /> Delete Bot
          </button>
        )}
        <button
          onClick={() => nav('/ai-dialer?tab=campaigns')}
          className="btn-ghost"
        >
          Cancel
        </button>
        <button
          onClick={() => saveMut.mutate('draft')}
          disabled={!bot.name.trim() || saveMut.isPending}
          className="btn-ghost flex items-center gap-2"
          title={!bot.name.trim() ? 'Add a bot name first' : 'Save without making this bot available to campaigns'}
        >
          {saveMut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save Draft
        </button>
        <button
          onClick={() => saveMut.mutate('ready')}
          disabled={!bot.name.trim() || !bot.voiceId || saveMut.isPending}
          className="btn-primary flex items-center gap-2"
          title={
            !bot.name.trim() ? 'Add a bot name first' :
            !bot.voiceId    ? 'Pick a voice in the Voice section first' :
            'Mark this bot ready and make it selectable from campaigns'
          }
        >
          {saveMut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
          Save & Mark Ready
        </button>
      </div>

      {confirmDelete && (
        <DeleteBotConfirm
          name={bot.name || 'this bot'}
          busy={deleteMut.isPending}
          onCancel={() => !deleteMut.isPending && setConfirmDelete(false)}
          onConfirm={() => deleteMut.mutate()}
        />
      )}
    </div>
  );
}

function DeleteBotConfirm({ name, busy, onConfirm, onCancel }) {
  const cancelRef = useRef(null);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onCancel?.(); };
    document.addEventListener('keydown', onKey);
    cancelRef.current?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-bot-title"
      className="fixed inset-0 z-60 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4"
      onClick={onCancel}
    >
      <div onClick={(e) => e.stopPropagation()} className="panel p-5 max-w-sm w-full space-y-4">
        <div id="delete-bot-title" className="font-display font-bold text-lg text-white">Delete this bot?</div>
        <div className="text-sm text-white/70">
          You're about to delete <span className="text-white">"{name}"</span>. Any campaigns using it will need to be re-pointed. This cannot be undone.
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <button ref={cancelRef} onClick={onCancel} disabled={busy} className="btn-ghost">Cancel</button>
          <button
            onClick={onConfirm}
            disabled={busy}
            className="px-3 py-2 rounded-md text-sm font-medium text-white bg-rose-500/80 hover:bg-rose-500 disabled:opacity-60 flex items-center gap-2"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            Delete bot
          </button>
        </div>
      </div>
    </div>
  );
}

function SectionHeader({ icon: Icon, title, sub }) {
  return (
    <div className="flex items-center gap-3 mb-3">
      <div className="w-8 h-8 rounded-lg bg-civix-purple/20 flex items-center justify-center">
        <Icon className="w-4 h-4 text-civix-purple" />
      </div>
      <div>
        <div className="text-sm font-semibold text-white">{title}</div>
        {sub && <div className="text-xs text-white/50">{sub}</div>}
      </div>
    </div>
  );
}

// ── A. Identity ───────────────────────────────────────────────────────────
function SectionA({ bot, update }) {
  const photoRef = useRef(null);
  const onPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => update({ photo: reader.result });
    reader.readAsDataURL(f);
  };
  return (
    <div className="panel p-5">
      <SectionHeader icon={Bot} title="Identity" sub="What's this bot called and what does it do?" />
      <div className="grid md:grid-cols-3 gap-4">
        <div className="flex flex-col items-center gap-2">
          <button
            type="button"
            onClick={() => photoRef.current?.click()}
            aria-label="Upload bot photo"
            className="w-24 h-24 rounded-2xl bg-civix-purple/20 flex items-center justify-center overflow-hidden border-2 border-dashed border-civix-purple/30 hover:border-civix-purple/60"
          >
            {bot.photo
              ? <img src={bot.photo} alt="" className="w-full h-full object-cover" />
              : <Bot className="w-8 h-8 text-civix-purple/60" />}
          </button>
          <input ref={photoRef} type="file" accept="image/*" className="hidden" onChange={onPhoto} />
          <button onClick={() => photoRef.current?.click()} className="text-xs text-civix-cyan hover:underline">Upload photo</button>
        </div>
        <div className="md:col-span-2 space-y-3">
          <div>
            <label className="form-label">Bot name</label>
            <input className="input w-full" value={bot.name} onChange={(e) => update({ name: e.target.value })} placeholder="e.g. Final Expense Closer — TX" />
          </div>
          <div>
            <label className="form-label">Short description</label>
            <textarea className="input w-full" rows={2} value={bot.description} onChange={(e) => update({ description: e.target.value })} placeholder="What this bot is for, in one sentence." />
          </div>
        </div>
      </div>
    </div>
  );
}

// ── B. Voice ──────────────────────────────────────────────────────────────
function SectionB({ bot, update, trainedVoices }) {
  const [recording, setRecording] = useState(false);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const inputRef = useRef(null);

  const cloneMut = useMutation({
    mutationFn: async ({ file, name }) => {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('name', name);
      const r = await api.post('/dialer/v1/voices/clone', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return r.data;
    },
    onSuccess: (r) => {
      toast.success('Voice cloned');
      const v = r?.data;
      if (v?.voiceId) update({ voiceSource: 'clone', voiceId: v.voiceId, voiceLabel: v.name || 'My cloned voice' });
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Clone failed'),
  });

  const upload = (file) => {
    const name = (bot.name || 'My Voice').slice(0, 50);
    cloneMut.mutate({ file, name });
  };

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => chunksRef.current.push(e.data);
      rec.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        const file = new File([blob], 'voice-clone.webm', { type: 'audio/webm' });
        upload(file);
        setRecording(false);
      };
      recRef.current = rec;
      rec.start();
      setRecording(true);
    } catch (e) {
      toast.error('Microphone unavailable: ' + e.message);
    }
  };
  const stopRec = () => {
    try { recRef.current?.stop(); } catch (_) {}
  };

  return (
    <div className="panel p-5">
      <SectionHeader icon={Volume2} title="Voice" sub="Stock voice, clone your own, or pick one you've already cloned." />
      <div className="flex flex-wrap gap-2 mb-4">
        {[
          { id: 'stock',    label: 'Stock voice' },
          { id: 'clone',    label: 'Clone my voice' },
          { id: 'existing', label: 'My trained voices' },
        ].map((m) => (
          <button
            key={m.id}
            onClick={() => update({ voiceSource: m.id })}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium ${bot.voiceSource === m.id ? 'bg-civix-purple/20 text-white border border-civix-purple/40' : 'text-white/50 border border-white/10 hover:bg-white/5'}`}
          >
            {m.label}
          </button>
        ))}
      </div>

      {bot.voiceSource === 'stock' && (
        <div className="grid md:grid-cols-2 gap-2">
          {STOCK_VOICES.map((v) => (
            <button
              key={v.id}
              onClick={() => update({ voiceId: v.id, voiceLabel: v.label })}
              className={`panel p-3 text-left ${bot.voiceId === v.id ? 'border-civix-purple/40' : ''}`}
            >
              <div className="text-sm text-white">{v.label}</div>
              <div className="text-xs text-white/40">ElevenLabs stock</div>
            </button>
          ))}
        </div>
      )}

      {bot.voiceSource === 'clone' && (
        <div className="space-y-3">
          <div
            onClick={() => inputRef.current?.click()}
            className="rounded-lg border-2 border-dashed border-civix-border/60 hover:border-civix-purple/40 p-6 text-center cursor-pointer"
          >
            <Upload className="w-6 h-6 mx-auto mb-2 text-white/40" />
            <div className="text-sm text-white/70">
              {cloneMut.isPending ? 'Uploading & cloning…' : 'Drop an audio file (30s–5min, MP3/WAV/M4A) or click to browse'}
            </div>
          </div>
          <input
            ref={inputRef}
            type="file"
            accept=".mp3,.wav,.m4a,audio/*"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); }}
          />
          <div className="flex items-center gap-2">
            {!recording ? (
              <button onClick={startRec} className="btn-secondary flex items-center gap-2">
                <Mic className="w-4 h-4" /> Record from browser
              </button>
            ) : (
              <button onClick={stopRec} className="btn-primary flex items-center gap-2">
                <X className="w-4 h-4" /> Stop recording
              </button>
            )}
            {bot.voiceSource === 'clone' && bot.voiceId && !cloneMut.isPending && (
              <div className="text-sm text-green-400 flex items-center gap-1">
                <CheckCircle2 className="w-4 h-4" /> Cloned: {bot.voiceLabel}
              </div>
            )}
          </div>
        </div>
      )}

      {bot.voiceSource === 'existing' && (
        <div>
          {trainedVoices.length === 0 ? (
            <div className="panel p-4 text-sm text-white/50">No trained voices yet. Clone one above first.</div>
          ) : (
            <div className="grid md:grid-cols-2 gap-2">
              {trainedVoices.map((v) => (
                <button
                  key={v.voiceId}
                  onClick={() => update({ voiceId: v.voiceId, voiceLabel: v.name })}
                  className={`panel p-3 text-left ${bot.voiceId === v.voiceId ? 'border-civix-purple/40' : ''}`}
                >
                  <div className="text-sm text-white">{v.name || 'Cloned voice'}</div>
                  <div className="text-xs text-white/40">{v.voiceId}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── OpenAI Realtime voice (AI test call only) ── */}
      <div className="mt-5 pt-4 border-t border-white/10">
        <div className="text-xs font-semibold text-white/60 uppercase tracking-wider mb-1">
          AI Test Call Voice (OpenAI Realtime)
        </div>
        <div className="text-xs text-white/40 mb-2">
          Used only for the browser test call — separate from the ElevenLabs voice above.
        </div>
        <select
          value={bot.realtimeVoice || 'alloy'}
          onChange={(e) => update({ realtimeVoice: e.target.value })}
          className="w-full rounded-lg bg-white/5 border border-white/10 text-sm text-white px-3 py-2 focus:outline-hidden focus:border-civix-purple/60"
        >
          {REALTIME_VOICES.map((v) => (
            <option key={v.id} value={v.id}>{v.label}</option>
          ))}
        </select>
      </div>
    </div>
  );
}

// ── C. Script + Personality ───────────────────────────────────────────────
function SectionC({ bot, update }) {
  const fileRef = useRef(null);
  const qc = useQueryClient();
  // stage drives the dropzone status text: null|'uploading'|'queued'|
  // 'transcribing'|'extracting'. We no longer race two booleans (extracting
  // vs transcribing) because the backend now tells us the exact phase via
  // the poll response.
  const [stage, setStage] = useState(null);
  const [compressing, setCompressing] = useState(false);
  const [compressProgress, setCompressProgress] = useState(0);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    let fileToUpload = file;
    const plan = planCompression(file);
    if (plan.action === 'reject') {
      toast.error(plan.reason, { duration: 8000 });
      if (fileRef.current) fileRef.current.value = '';
      return;
    }
    if (plan.action === 'compress') {
      setCompressing(true);
      setCompressProgress(0);
      try {
        const result = await compressAudioFile(file, {
          onProgress: (p) => setCompressProgress(p),
        });
        fileToUpload = result.file;
        toast.success(formatCompressionSummary(result.originalSize, result.compressedSize), { duration: 5000 });
      } catch (compressErr) {
        // eslint-disable-next-line no-console
        console.warn('[compress] failed, uploading original:', compressErr);
      } finally {
        setCompressing(false);
        setCompressProgress(0);
      }
    }

    setStage('uploading');
    try {
      const fd = new FormData();
      fd.append('file', fileToUpload);
      if (bot._id) fd.append('botId', bot._id);
      // POST returns 202 + jobId; the heavy work runs server-side and we
      // poll until it finishes. This avoids Railway's ~100s edge timeout
      // killing long Whisper transcriptions in flight.
      const r = await api.post('/dialer/v1/script/extract', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const jobId = r.data?.jobId;
      if (!jobId) throw new Error('no jobId returned');
      setStage('queued');
      const job = await pollExtractJob(jobId, { onStatusChange: setStage });
      if (job.status === 'failed') {
        const code = job.whisperCode ? ` [${job.whisperCode}]` : '';
        toast.error(`${job.detail || job.error || 'Extract failed'}${code}`, { duration: 8000 });
        // eslint-disable-next-line no-console
        console.error('[script extract] job failed:', job);
        return;
      }
      const x = job.data || {};
      update({
        openingLine:         x.openingLine || bot.openingLine,
        qualifyingQuestions: Array.isArray(x.qualifyingQuestions) ? x.qualifyingQuestions : bot.qualifyingQuestions,
        objectionHandlers:   Array.isArray(x.objectionHandlers) ? x.objectionHandlers : bot.objectionHandlers,
        closingScript:       x.closingScript || bot.closingScript,
        personalityNotes:    x.personalityNotes || bot.personalityNotes,
      });
      toast.success('Script extracted');
      if (bot._id) {
        qc.invalidateQueries({ queryKey: ['bot-training-materials', bot._id] });
      } else {
        toast('Save the bot to start tracking training history.', { icon: '💡', duration: 6000 });
      }
    } catch (err) {
      const errData = err.response?.data || {};
      const baseMsg = errData.detail || errData.error || err.message || 'Extract failed';
      const code = errData.whisperCode ? ` [${errData.whisperCode}]` : '';
      toast.error(`${baseMsg}${code}`, { duration: 8000 });
      if (Object.keys(errData).length) {
        // eslint-disable-next-line no-console
        console.error('[script extract] server error response:', errData);
      }
    } finally {
      setStage(null);
    }
  };

  const addQ = () => update({ qualifyingQuestions: [...(bot.qualifyingQuestions || []), ''] });
  const setQ = (i, v) => {
    const arr = [...(bot.qualifyingQuestions || [])];
    arr[i] = v;
    update({ qualifyingQuestions: arr });
  };
  const removeQ = (i) => {
    const arr = [...(bot.qualifyingQuestions || [])];
    arr.splice(i, 1);
    update({ qualifyingQuestions: arr });
  };

  const addObj = () => update({ objectionHandlers: [...(bot.objectionHandlers || []), { objection: '', response: '' }] });
  const setObj = (i, patch) => {
    const arr = [...(bot.objectionHandlers || [])];
    arr[i] = { ...arr[i], ...patch };
    update({ objectionHandlers: arr });
  };
  const removeObj = (i) => {
    const arr = [...(bot.objectionHandlers || [])];
    arr.splice(i, 1);
    update({ objectionHandlers: arr });
  };

  return (
    <div className="panel p-5">
      <SectionHeader icon={FileText} title="Script + Personality" sub="Upload a PDF, Word doc, or recorded video/audio to auto-extract talking points, or edit by hand." />

      <div
        onClick={() => fileRef.current?.click()}
        className="rounded-lg border-2 border-dashed border-civix-border/60 hover:border-civix-purple/40 p-4 text-center cursor-pointer mb-4"
      >
        <Upload className="w-5 h-5 mx-auto mb-1 text-white/40" />
        <div className="text-xs text-white/70">
          {compressing
            ? `Compressing recording… ${Math.round((compressProgress || 0) * 100)}%`
            : stage === 'uploading'
              ? 'Uploading…'
              : stage === 'queued'
                ? 'Queued…'
                : stage === 'transcribing'
                  ? 'Transcribing recording…'
                  : stage === 'extracting'
                    ? 'Extracting talking points…'
                    : 'Drop a script (PDF, Word, TXT, MD) or a recorded call (video/audio) — click to browse'}
        </div>
      </div>
      <input ref={fileRef} type="file" accept=".pdf,.txt,.md,.docx,.doc,video/*,audio/*,.mp4,.mov,.webm,.m4a,.mp3,.wav" className="hidden" onChange={onFile} />

      <div className="space-y-4">
        <div>
          <label className="form-label">Opening line</label>
          <textarea className="input w-full" rows={2} value={bot.openingLine} onChange={(e) => update({ openingLine: e.target.value })} placeholder="Hi, this is …" />
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="form-label mb-0">Qualifying questions</label>
            <button onClick={addQ} className="text-xs text-civix-cyan hover:underline flex items-center gap-1"><Plus className="w-3 h-3" /> Add</button>
          </div>
          <div className="space-y-2">
            {(bot.qualifyingQuestions || []).map((q, i) => (
              <div key={i} className="flex gap-2">
                <input className="input flex-1" value={q} onChange={(e) => setQ(i, e.target.value)} />
                <button onClick={() => removeQ(i)} className="p-2 text-white/40 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            {(bot.qualifyingQuestions || []).length === 0 && <div className="text-xs text-white/40">No questions yet.</div>}
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="form-label mb-0">Objection handlers</label>
            <button onClick={addObj} className="text-xs text-civix-cyan hover:underline flex items-center gap-1"><Plus className="w-3 h-3" /> Add</button>
          </div>
          <div className="space-y-2">
            {(bot.objectionHandlers || []).map((o, i) => (
              <div key={i} className="grid grid-cols-2 gap-2">
                <input className="input" placeholder="Objection" value={o.objection} onChange={(e) => setObj(i, { objection: e.target.value })} />
                <div className="flex gap-2">
                  <input className="input flex-1" placeholder="Response" value={o.response} onChange={(e) => setObj(i, { response: e.target.value })} />
                  <button onClick={() => removeObj(i)} className="p-2 text-white/40 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
            {(bot.objectionHandlers || []).length === 0 && <div className="text-xs text-white/40">No objection handlers yet.</div>}
          </div>
        </div>

        <div>
          <label className="form-label">Closing script</label>
          <textarea className="input w-full" rows={2} value={bot.closingScript} onChange={(e) => update({ closingScript: e.target.value })} />
        </div>

        <div>
          <label className="form-label">Personality notes</label>
          <textarea className="input w-full" rows={3} value={bot.personalityNotes} onChange={(e) => update({ personalityNotes: e.target.value })} placeholder="Warm. Confident. Never high-pressure." />
        </div>
      </div>
    </div>
  );
}

// ── D. Few-shot examples ──────────────────────────────────────────────────
function SectionD({ bot, update }) {
  const ref = useRef(null);
  const [compressing, setCompressing] = useState(false);
  const [compressProgress, setCompressProgress] = useState(0);
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      let workingFile = file;
      const plan = planCompression(file);
      if (plan.action === 'reject') {
        toast.error(plan.reason, { duration: 8000 });
        if (ref.current) ref.current.value = '';
        return;
      }
      if (plan.action === 'compress') {
        setCompressing(true);
        setCompressProgress(0);
        try {
          const result = await compressAudioFile(file, {
            onProgress: (p) => setCompressProgress(p),
          });
          workingFile = result.file;
          toast.success(formatCompressionSummary(result.originalSize, result.compressedSize), { duration: 5000 });
        } catch (compressErr) {
          // eslint-disable-next-line no-console
          console.warn('[compress] failed, keeping original:', compressErr);
        } finally {
          setCompressing(false);
          setCompressProgress(0);
        }
      }

      const reader = new FileReader();
      reader.onload = () => {
        const content = String(reader.result || '').slice(0, 12000);
        const ex = {
          title: workingFile.name,
          content,
          source: /\.(mp3|wav|m4a)$/i.test(workingFile.name) ? 'audio' : 'transcript',
        };
        update({ fewShotExamples: [...(bot.fewShotExamples || []), ex] });
        toast.success('Example added');
      };
      reader.onerror = () => {
        const msg = reader.error?.message || reader.error?.name || 'FileReader error';
        toast.error(`Failed to read ${workingFile.name}: ${msg}`, { duration: 8000 });
        // eslint-disable-next-line no-console
        console.error('[few-shot] FileReader error:', reader.error);
      };
      if (/\.(txt|md|vtt|srt)$/i.test(workingFile.name)) reader.readAsText(workingFile);
      else {
        // For audio, just record the filename — full transcription is server-side
        // training pipeline territory (existing /bots/:id/training endpoint).
        update({ fewShotExamples: [...(bot.fewShotExamples || []), { title: workingFile.name, content: '(audio — will be transcribed)', source: 'audio' }] });
        toast('Audio queued — transcription happens server-side', { icon: '🎧' });
      }
    } catch (err) {
      const errData = err.response?.data || {};
      const baseMsg = errData.detail || errData.error || err.message || 'Upload failed';
      toast.error(baseMsg, { duration: 8000 });
      if (Object.keys(errData).length) {
        // eslint-disable-next-line no-console
        console.error('[few-shot] server error response:', errData);
      }
    }
  };
  const remove = (i) => {
    const arr = [...(bot.fewShotExamples || [])];
    arr.splice(i, 1);
    update({ fewShotExamples: arr });
  };
  return (
    <div className="panel p-5">
      <SectionHeader icon={MessageCircle} title="Few-shot examples (optional)" sub="Upload past call recordings or transcripts — the bot learns from your best closes." />
      <div
        onClick={() => ref.current?.click()}
        className="rounded-lg border-2 border-dashed border-civix-border/60 hover:border-civix-purple/40 p-4 text-center cursor-pointer mb-3"
      >
        <Upload className="w-5 h-5 mx-auto mb-1 text-white/40" />
        <div className="text-xs text-white/70">
          {compressing
            ? `Compressing recording… ${Math.round((compressProgress || 0) * 100)}%`
            : 'Drop transcripts (.txt/.vtt/.docx), audio (.mp3/.wav), or call recordings (.mp4/.mov/.webm) — multiple allowed'}
        </div>
      </div>
      <input ref={ref} type="file" accept=".txt,.vtt,.srt,.md,.docx,.doc,.mp3,.wav,.m4a,.mp4,.mov,.webm" className="hidden" onChange={onFile} />
      <div className="space-y-2">
        {(bot.fewShotExamples || []).map((ex, i) => (
          <div key={i} className="flex items-center gap-2 text-xs bg-white/5 rounded-sm px-3 py-2">
            <span className="text-white/40 uppercase text-[10px]">{ex.source}</span>
            <span className="flex-1 truncate text-white/80">{ex.title}</span>
            <button onClick={() => remove(i)} className="text-white/40 hover:text-red-400"><Trash2 className="w-3 h-3" /></button>
          </div>
        ))}
        {(bot.fewShotExamples || []).length === 0 && <div className="text-xs text-white/40">No examples yet.</div>}
      </div>
    </div>
  );
}

// ── E. Test ───────────────────────────────────────────────────────────────
function SectionE({ bot }) {
  const [open, setOpen] = useState(false);
  const [audioOpen, setAudioOpen] = useState(false);
  const [mode, setMode] = useState('text');
  const [history, setHistory] = useState([]);
  const [draft, setDraft] = useState('');
  const draftRef = useRef(null);

  // Escape closes the text-test modal; auto-focus the input when it opens.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    draftRef.current?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const sendMut = useMutation({
    mutationFn: (message) => apiPost(`/dialer/v1/bots/${bot._id}/test`, {
      message,
      history,
    }),
    onSuccess: (r) => {
      const reply = r?.data?.reply || '(no reply)';
      setHistory((h) => [...h, { role: 'bot', content: reply }]);
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Test failed'),
  });

  const submit = () => {
    if (!draft.trim()) return;
    if (!bot._id) {
      toast.error('Save the bot first to enable test mode.');
      return;
    }
    const userMsg = draft.trim();
    setHistory((h) => [...h, { role: 'user', content: userMsg }]);
    setDraft('');
    sendMut.mutate(userMsg);
  };

  const startTest = () => {
    if (!bot._id) { toast.error('Save the bot first to enable test mode.'); return; }
    if (mode === 'audio') setAudioOpen(true);
    else setOpen(true);
  };

  return (
    <div className="panel p-5">
      <SectionHeader icon={Sparkles} title="Test your bot" sub="Preview the bot in text or live audio mode." />
      <div className="flex items-center gap-2 mb-3">
        <button
          type="button"
          onClick={() => setMode('text')}
          className={`btn-sm ${mode === 'text' ? 'btn-primary' : 'btn-ghost'}`}
        >Text Mode</button>
        <button
          type="button"
          onClick={() => setMode('audio')}
          className={`btn-sm ${mode === 'audio' ? 'btn-primary' : 'btn-ghost'}`}
        >Audio Mode</button>
        {mode === 'audio' && <span className="text-xs text-white/40 ml-2">Live mic ↔ AI. Capped at $0.50 / test.</span>}
      </div>
      <button onClick={startTest} disabled={!bot._id} className="btn-primary flex items-center gap-2 disabled:opacity-40">
        <Play className="w-4 h-4" /> {mode === 'audio' ? 'Start audio test' : 'Start test conversation'}
      </button>
      {!bot._id && <div className="text-xs text-white/40 mt-2">Save as Draft first to enable test mode.</div>}
      {audioOpen && <BotAudioTest botId={bot._id} onClose={() => setAudioOpen(false)} />}

      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={`Test conversation with ${bot.name || 'bot'}`}
          className="fixed inset-0 bg-black/60 backdrop-blur-xs z-50 flex items-center justify-center p-4"
          onClick={() => setOpen(false)}
        >
          <div onClick={(e) => e.stopPropagation()} className="panel w-full max-w-lg max-h-[80vh] flex flex-col">
            <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
              <div className="text-sm font-semibold text-white">Testing {bot.name}</div>
              <button onClick={() => setOpen(false)} aria-label="Close test conversation" className="text-white/50 hover:text-white"><X className="w-4 h-4" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              {history.length === 0 && <div className="text-xs text-white/40 text-center">Type a message as a lead — the bot will respond.</div>}
              {history.map((m, i) => (
                <div key={i} className={`text-sm ${m.role === 'user' ? 'text-right' : ''}`}>
                  <span className={`inline-block rounded-lg px-3 py-2 ${m.role === 'user' ? 'bg-civix-cyan/20 text-white' : 'bg-white/10 text-white/90'}`}>
                    {m.content}
                  </span>
                </div>
              ))}
              {sendMut.isPending && <div className="text-xs text-white/40">…thinking</div>}
            </div>
            <form
              onSubmit={(e) => { e.preventDefault(); submit(); }}
              className="px-4 py-3 border-t border-white/10 flex gap-2"
            >
              <input
                ref={draftRef}
                className="input flex-1"
                placeholder="Talk to the bot…"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                aria-label="Message to send to bot"
              />
              <button type="submit" className="btn-primary" disabled={!draft.trim() || sendMut.isPending}>Send</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
